import { useState, useEffect } from 'react';
import { api } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/Select';
import { Skeleton } from '@/components/ui/Skeleton';
import { BarChart3, Clock, Wallet, FileText, TrendingUp } from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

interface AnalyticsPeriod {
  period: string;
  totalHours: number;
  totalChf: number;
  entryCount: number;
  avgRate: number;
}

interface AnalyticsData {
  periods: AnalyticsPeriod[];
  overall: {
    totalHours: number;
    totalChf: number;
    entryCount: number;
    avgRate: number;
  };
}

interface Project { id: string; name: string; }

const PERIOD_OPTIONS = [
  { value: 'week', label: 'Week' },
  { value: 'month', label: 'Month' },
  { value: 'quarter', label: 'Quarter' },
  { value: 'semester', label: 'Semester' },
  { value: 'year', label: 'Year' },
];

export default function DashboardAnalyticsSection() {
  const [period, setPeriod] = useState('month');
  const [projectId, setProjectId] = useState<string>('');
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api<Project[]>('/projects')
      .then((d) => setProjects(d || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const qs = new URLSearchParams({ period });
    if (projectId) qs.set('projectId', projectId);
    api<AnalyticsData>(`/dashboard/analytics?${qs.toString()}`)
      .then((d) => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [period, projectId]);

  const stats = data?.overall;

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="w-36 space-y-1">
          <label className="text-xs font-semibold uppercase tracking-wider text-[var(--color-muted-foreground)]">Period</label>
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              {PERIOD_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="w-56 space-y-1">
          <label className="text-xs font-semibold uppercase tracking-wider text-[var(--color-muted-foreground)]">Project</label>
          <Select value={projectId} onValueChange={setProjectId}>
            <SelectTrigger className="h-9 text-sm"><SelectValue placeholder="All projects" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="">All projects</SelectItem>
              {projects.map((p) => (
                <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Chart */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <BarChart3 className="h-4 w-4 text-[var(--color-primary)]" /> Analytics by {PERIOD_OPTIONS.find(o => o.value === period)?.label}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-2">
              <Skeleton className="h-64 w-full" />
            </div>
          ) : !data || data.periods.length === 0 ? (
            <div className="flex h-64 items-center justify-center text-sm text-[var(--color-muted-foreground)]">
              No data for the selected period
            </div>
          ) : (
            <div className="h-64 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.periods} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="period" tick={{ fontSize: 12 }} />
                  <YAxis yAxisId="hours" orientation="left" tick={{ fontSize: 12 }} />
                  <YAxis yAxisId="chf" orientation="right" tick={{ fontSize: 12 }} />
                  <Tooltip contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }} />
                  <Legend wrapperStyle={{ fontSize: '12px' }} />
                  <Bar yAxisId="hours" dataKey="totalHours" name="Hours" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                  <Bar yAxisId="chf" dataKey="totalChf" name="CHF" fill="#10b981" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Summary cards */}
      {loading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-[var(--color-muted-foreground)]">Total Hours</CardTitle>
              <Clock className="h-4 w-4 text-[var(--color-muted-foreground)]" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-[var(--color-primary)] font-mono tracking-tight">{stats?.totalHours?.toFixed(2) ?? '0.00'}h</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-[var(--color-muted-foreground)]">Total CHF</CardTitle>
              <Wallet className="h-4 w-4 text-[var(--color-muted-foreground)]" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-[var(--color-primary)] font-mono tracking-tight">{stats?.totalChf?.toFixed(2) ?? '0.00'}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-[var(--color-muted-foreground)]">Entries</CardTitle>
              <FileText className="h-4 w-4 text-[var(--color-muted-foreground)]" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-[var(--color-primary)] font-mono tracking-tight">{stats?.entryCount ?? 0}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-[var(--color-muted-foreground)]">Avg Rate</CardTitle>
              <TrendingUp className="h-4 w-4 text-[var(--color-muted-foreground)]" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-[var(--color-primary)] font-mono tracking-tight">{stats?.avgRate?.toFixed(2) ?? '0.00'}</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Period table */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <BarChart3 className="h-4 w-4 text-[var(--color-primary)]" /> Details by Period
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-2"><Skeleton className="h-8 w-full" /><Skeleton className="h-8 w-full" /></div>
          ) : !data || data.periods.length === 0 ? (
            <div className="py-8 text-center text-sm text-[var(--color-muted-foreground)]">
              No data available.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[var(--color-border)] text-left">
                    <th className="py-2 pr-4 font-semibold">Period</th>
                    <th className="py-2 pr-4 font-semibold text-right">Total Hours</th>
                    <th className="py-2 pr-4 font-semibold text-right">CHF</th>
                    <th className="py-2 pr-4 font-semibold text-right">Entries</th>
                    <th className="py-2 font-semibold text-right">Avg Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {data.periods.map((row, i) => (
                    <tr key={row.period} className={i !== data.periods.length - 1 ? 'border-b border-[var(--color-border)]' : ''}>
                      <td className="py-2 pr-4 font-medium">{row.period}</td>
                      <td className="py-2 pr-4 text-right font-mono">{row.totalHours.toFixed(2)}h</td>
                      <td className="py-2 pr-4 text-right font-mono">{row.totalChf.toFixed(2)}</td>
                      <td className="py-2 pr-4 text-right font-mono">{row.entryCount}</td>
                      <td className="py-2 text-right font-mono">{row.avgRate.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
